/**
 * Shared AJAX cart helper for all `sis-` pages/components (quiz product cards,
 * the cart page, cart drawer, and pre-purchase upsell widgets). Exposes
 * `window.SisCart`.
 *
 * Everything here is wired via event delegation on `document`/`document.body`
 * (never on a specific node), because `sections/sis-cart.liquid`,
 * `sections/sis-cart-shipping-bar.liquid` and `sections/sis-cart-drawer.liquid`
 * get *replaced wholesale* after a cart mutation (see `refreshSections`, which
 * re-fetches the sections' rendered HTML via Shopify's Section Rendering API
 * so Liquid-only content — Judge.me badges, product type badges, discount
 * rows, free-shipping copy, order bumps — stays correct without duplicating
 * that logic in JS). Listeners bound directly to nodes inside those sections
 * would be lost on refresh; delegated listeners on `document` survive it.
 */
(function () {
  function jsonHeaders() {
    return { 'Content-Type': 'application/json', Accept: 'application/json' };
  }

  function handleResponse(res) {
    if (!res.ok) {
      return res.json().then(function (err) {
        throw new Error((err && err.description) || 'cart_request_failed');
      });
    }
    return res.json();
  }

  function fetchCart() {
    return fetch('/cart.js').then(function (res) {
      return res.json();
    });
  }

  /**
   * Hidden line-item properties for order bumps. Keys starting with `_` are
   * omitted from the customer-facing checkout and still stored on the order.
   * Only elements marked `data-sis-bump-surface` are bumps; other quick-adds
   * stay unmarked.
   */
  function trackingProperties(el) {
    if (!el || !el.getAttribute) return null;
    var surface = el.getAttribute('data-sis-bump-surface');
    if (!surface && el.closest) {
      el = el.closest('[data-sis-bump-surface]') || el;
      surface = el.getAttribute('data-sis-bump-surface');
    }
    if (!surface) return null;
    var props = {
      _sis_source: 'order_bump',
      _sis_surface: surface
    };
    var parentId = el.getAttribute('data-sis-bump-parent');
    if (parentId) props._sis_parent = String(parentId);
    var parentTitle = el.getAttribute('data-sis-bump-parent-title');
    if (parentTitle) props._sis_parent_title = parentTitle;
    var parentUrl = el.getAttribute('data-sis-bump-parent-url');
    if (parentUrl) props._sis_parent_url = parentUrl;
    var productUrl = el.getAttribute('data-sis-bump-product-url');
    if (productUrl) props._sis_product_url = productUrl;
    return props;
  }

  function addItem(variantId, quantity, opts) {
    quantity = quantity || 1;
    opts = opts || {};
    var body = { id: variantId, quantity: quantity };
    if (opts.properties) body.properties = opts.properties;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(body)
    })
      .then(handleResponse)
      .then(function (item) {
        return fetchCart().then(function (cart) {
          document.dispatchEvent(
            new CustomEvent('sis:cart:updated', {
              detail: { cart: cart, addedItem: item, skipDrawer: !!opts.skipDrawer }
            })
          );
          return cart;
        });
      });
  }

  function changeItem(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(handleResponse)
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('sis:cart:updated', { detail: { cart: cart } }));
        return cart;
      });
  }

  function removeItem(key) {
    return changeItem(key, 0);
  }

  function clearCart() {
    return fetch('/cart/clear.js', { method: 'POST', headers: jsonHeaders() })
      .then(handleResponse)
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('sis:cart:updated', { detail: { cart: cart } }));
        return cart;
      });
  }

  /**
   * Re-fetches the rendered HTML of every `[data-sis-section-id]` currently
   * on the page (via `?sections=id-a,id-b`) and swaps each one in whole.
   * Any `sis-` section that reacts to cart totals (cart items, free-shipping
   * bar, cart drawer) should mark its outermost element with
   * `data-sis-section-id="{{ section.id }}"` to participate.
   */
  function refreshSections() {
    var nodes = document.querySelectorAll('[data-sis-section-id]');
    if (!nodes.length) return Promise.resolve();

    var ids = [];
    nodes.forEach(function (el) {
      var id = el.getAttribute('data-sis-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });

    return fetch(window.location.pathname + window.location.search + (window.location.search ? '&' : '?') + 'sections=' + ids.map(encodeURIComponent).join(','))
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        ids.forEach(function (id) {
          var html = data[id];
          var target = document.querySelector('[data-sis-section-id="' + id + '"]');
          if (!html || !target) return;
          var oldList = target.querySelector('[data-sis-cart-recommendations-list]');
          if (oldList) destroyRecommendationsCarousel(oldList);
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-sis-section-id="' + id + '"]');
          if (fresh) {
            if (target.hasAttribute('data-sis-cart-drawer') && target.classList.contains('is-open')) {
              fresh.classList.add('is-open', 'sis-cart-drawer--no-anim');
              fresh.setAttribute('aria-hidden', 'false');
            }
            target.replaceWith(fresh);
            if (fresh.classList.contains('sis-cart-drawer--no-anim')) {
              requestAnimationFrame(function () {
                fresh.classList.remove('sis-cart-drawer--no-anim');
              });
            }
          }
        });
        fillCartRecommendations();
      });
  }

  function setPending(root, isPending) {
    if (!root) return;
    if (root.hasAttribute('data-sis-cart-drawer')) {
      root.classList.toggle('sis-cart-drawer--busy', !!isPending);
      return;
    }
    root.classList.toggle('sis-cart--pending', !!isPending);
  }

  // --- Money formatting (mirrors Shopify.formatMoney's supported tokens) ---
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '{{amount}}';

    function defaultTo(a, b) {
      return a == null ? b : a;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';
      return dollars + cents2;
    }

    switch (formatString.match(placeholder)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholder, value);
  }

  // Figma I314:2329;5:250 — icon/shopping-cart (upsell CTA, 20px)
  var SIS_CART_BUTTON_ICON =
    '<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M7.5 18.3333C7.96024 18.3333 8.33333 17.9602 8.33333 17.5C8.33333 17.0398 7.96024 16.6667 7.5 16.6667C7.03976 16.6667 6.66667 17.0398 6.66667 17.5C6.66667 17.9602 7.03976 18.3333 7.5 18.3333Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M16.6667 18.3333C17.1269 18.3333 17.5 17.9602 17.5 17.5C17.5 17.0398 17.1269 16.6667 16.6667 16.6667C16.2064 16.6667 15.8333 17.0398 15.8333 17.5C15.8333 17.9602 16.2064 18.3333 16.6667 18.3333Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M0.833333 0.833333H4.16667L6.4 11.9917C6.4762 12.3753 6.68492 12.72 6.98962 12.9652C7.29432 13.2105 7.67558 13.3408 8.06667 13.3333H16.1667C16.5578 13.3408 16.939 13.2105 17.2437 12.9652C17.5484 12.72 17.7571 12.3753 17.8333 11.9917L19.1667 5H5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  // --- Native product-recommendations fallback for the cart-page upsell box ---
  function upsellCardMarkup(product, moneyFormat) {
    var variant = (product.variants && product.variants[0]) || {};
    var image = product.featured_image || (product.images && product.images[0]);
    var compareHtml = '';
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      compareHtml = '<span class="sis-upsell-card__price-compare">' + formatMoney(variant.compare_at_price, moneyFormat) + '</span>';
    }
    var addButtonHtml = '';
    if (product.available) {
      addButtonHtml =
        '<button type="button" class="sis-btn sis-btn--primary sis-upsell-card__add" data-sis-quick-add data-variant-id="' +
        variant.id +
        '">' +
        SIS_CART_BUTTON_ICON +
        'Adaugă în coș</button>';
    }
    return (
      '<div class="sis-cart__recommendations-slide" data-sis-cart-rec-id="' + product.id + '">' +
      '<div class="sis-upsell-card">' +
      '<a href="' + product.url + '" class="sis-upsell-card__media">' +
      (image ? '<img src="' + image + '" width="130" alt="' + (product.title || '').replace(/"/g, '&quot;') + '" loading="lazy">' : '') +
      '</a>' +
      '<div class="sis-upsell-card__body">' +
      '<a href="' + product.url + '" class="sis-upsell-card__title">' + product.title + '</a>' +
      '<div class="sis-upsell-card__price">' +
      '<span class="sis-upsell-card__price-current">' + formatMoney(variant.price, moneyFormat) + '</span>' +
      compareHtml +
      '</div>' +
      addButtonHtml +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  var carouselInstances = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  var carouselFallback = typeof WeakMap === 'undefined' ? [] : null;

  function getCarouselState(list) {
    if (carouselInstances) return carouselInstances.get(list);
    for (var i = 0; i < carouselFallback.length; i++) {
      if (carouselFallback[i].list === list) return carouselFallback[i];
    }
    return null;
  }

  function setCarouselState(list, state) {
    if (carouselInstances) {
      carouselInstances.set(list, state);
      return;
    }
    for (var i = 0; i < carouselFallback.length; i++) {
      if (carouselFallback[i].list === list) {
        carouselFallback[i] = state;
        return;
      }
    }
    carouselFallback.push(state);
  }

  function destroyRecommendationsCarousel(list) {
    if (!list) return;
    var state = getCarouselState(list);
    if (!state) return;

    if (state.autoTimer) window.clearInterval(state.autoTimer);
    if (state.pauseTimer) window.clearTimeout(state.pauseTimer);
    if (state.scrollTimer) window.clearTimeout(state.scrollTimer);
    if (state.resizeTimer) window.clearTimeout(state.resizeTimer);

    list.removeEventListener('scroll', state.onScroll);
    list.removeEventListener('pointerdown', state.onPointerDown);
    window.removeEventListener('pointermove', state.onPointerMove);
    window.removeEventListener('pointerup', state.onPointerUp);
    window.removeEventListener('pointercancel', state.onPointerUp);
    window.removeEventListener('resize', state.onResize);

    if (state.carousel) {
      state.carousel.removeEventListener('mouseenter', state.onMouseEnter);
      state.carousel.removeEventListener('mouseleave', state.onMouseLeave);
      state.carousel.removeEventListener('focusin', state.onFocusIn);
      state.carousel.removeEventListener('focusout', state.onFocusOut);
    }

    if (carouselInstances) {
      carouselInstances.delete(list);
    } else if (carouselFallback) {
      carouselFallback = carouselFallback.filter(function (entry) {
        return entry.list !== list;
      });
    }
  }

  function getCarouselSlideCount(list) {
    return list.querySelectorAll('.sis-cart__recommendations-slide').length;
  }

  function getCarouselSlideWidth(list) {
    return list.clientWidth || 0;
  }

  function getCarouselActiveIndex(list) {
    var slideWidth = getCarouselSlideWidth(list);
    var slideCount = getCarouselSlideCount(list);
    if (!slideWidth || slideCount <= 0) return 0;
    return Math.min(slideCount - 1, Math.max(0, Math.round(list.scrollLeft / slideWidth)));
  }

  function scrollCarouselToIndex(list, index, smooth) {
    var slideWidth = getCarouselSlideWidth(list);
    var slideCount = getCarouselSlideCount(list);
    if (!slideWidth || slideCount <= 0) return;
    var targetIndex = Math.min(slideCount - 1, Math.max(0, index));
    list.scrollTo({
      left: targetIndex * slideWidth,
      behavior: smooth ? 'smooth' : 'auto'
    });
  }

  function setCarouselActiveDot(dotsWrap, index) {
    var dots = dotsWrap.querySelectorAll('.sis-cart__recommendations-dot');
    dots.forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }

  function pauseCarouselAuto(state) {
    state.autoPaused = true;
    window.clearTimeout(state.pauseTimer);
    state.pauseTimer = window.setTimeout(function () {
      state.autoPaused = false;
    }, 10000);
  }

  function startCarouselAuto(list, state, dotsWrap) {
    window.clearInterval(state.autoTimer);
    if (getCarouselSlideCount(list) <= 1) return;

    state.autoTimer = window.setInterval(function () {
      if (state.autoPaused || state.isDragging) return;
      var nextIndex = (getCarouselActiveIndex(list) + 1) % getCarouselSlideCount(list);
      scrollCarouselToIndex(list, nextIndex, true);
    }, 5000);
  }

  /**
   * Single-slide carousel for the recommendations box (Figma node 314:2357).
   * Supports dot navigation, drag/swipe, scroll-snap, and auto-advance.
   * Re-inits cleanly when slides are streamed in via fillCartRecommendations
   * or when the cart section is re-rendered after AJAX mutations.
   */
  function initRecommendationsCarousel() {
    var carousel = document.querySelector('[data-sis-cart-recommendations-carousel]');
    var list = document.querySelector('[data-sis-cart-recommendations-list]');
    var dotsWrap = document.querySelector('[data-sis-cart-recommendations-dots]');
    if (!carousel || !list || !dotsWrap) return;

    destroyRecommendationsCarousel(list);

    var slides = list.querySelectorAll('.sis-cart__recommendations-slide');
    dotsWrap.innerHTML = '';
    dotsWrap.hidden = slides.length <= 1;

    slides.forEach(function (_, index) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'sis-cart__recommendations-dot' + (index === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', 'Produsul recomandat ' + (index + 1));
      dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      dot.addEventListener('click', function () {
        var carouselState = getCarouselState(list);
        if (carouselState) {
          pauseCarouselAuto(carouselState);
          carouselState.activeIndex = index;
          setCarouselActiveDot(dotsWrap, index);
        }
        scrollCarouselToIndex(list, index, true);
      });
      dotsWrap.appendChild(dot);
    });

    var state = {
      list: list,
      carousel: carousel,
      dotsWrap: dotsWrap,
      activeIndex: 0,
      isDragging: false,
      dragStartX: 0,
      scrollStartLeft: 0,
      autoPaused: false,
      autoTimer: null,
      pauseTimer: null,
      scrollTimer: null,
      resizeTimer: null
    };

    state.syncDots = function () {
      var activeIndex = getCarouselActiveIndex(list);
      if (activeIndex === state.activeIndex) return;
      state.activeIndex = activeIndex;
      setCarouselActiveDot(dotsWrap, activeIndex);
    };

    state.onScroll = function () {
      if (state.isDragging) return;
      window.clearTimeout(state.scrollTimer);
      state.scrollTimer = window.setTimeout(function () {
        state.syncDots();
      }, 60);
    };

    state.onResize = function () {
      window.clearTimeout(state.resizeTimer);
      state.resizeTimer = window.setTimeout(function () {
        scrollCarouselToIndex(list, getCarouselActiveIndex(list), false);
        state.syncDots();
      }, 120);
    };

    state.onPointerDown = function (e) {
      if (e.pointerType !== 'mouse') return;
      if (e.button !== 0) return;
      if (e.target.closest('a, button, input, textarea, select, label')) return;
      if (getCarouselSlideCount(list) <= 1) return;

      state.isDragging = true;
      state.dragStartX = e.clientX;
      state.scrollStartLeft = list.scrollLeft;
      state.autoPaused = true;
      list.classList.add('is-dragging');
      list.setPointerCapture(e.pointerId);
      pauseCarouselAuto(state);
    };

    state.onPointerMove = function (e) {
      if (!state.isDragging) return;
      list.scrollLeft = state.scrollStartLeft - (e.clientX - state.dragStartX);
    };

    state.onPointerUp = function (e) {
      if (!state.isDragging) return;
      state.isDragging = false;
      list.classList.remove('is-dragging');
      if (list.hasPointerCapture(e.pointerId)) {
        list.releasePointerCapture(e.pointerId);
      }
      scrollCarouselToIndex(list, getCarouselActiveIndex(list), true);
      state.syncDots();
      pauseCarouselAuto(state);
    };

    state.onMouseEnter = function () {
      state.autoPaused = true;
    };

    state.onMouseLeave = function () {
      state.autoPaused = false;
    };

    state.onFocusIn = function () {
      state.autoPaused = true;
    };

    state.onFocusOut = function (e) {
      if (!carousel.contains(e.relatedTarget)) {
        state.autoPaused = false;
      }
    };

    list.addEventListener('scroll', state.onScroll, { passive: true });
    list.addEventListener('pointerdown', state.onPointerDown);
    window.addEventListener('pointermove', state.onPointerMove);
    window.addEventListener('pointerup', state.onPointerUp);
    window.addEventListener('pointercancel', state.onPointerUp);
    window.addEventListener('resize', state.onResize);

    carousel.addEventListener('mouseenter', state.onMouseEnter);
    carousel.addEventListener('mouseleave', state.onMouseLeave);
    carousel.addEventListener('focusin', state.onFocusIn);
    carousel.addEventListener('focusout', state.onFocusOut);

    setCarouselState(list, state);
    scrollCarouselToIndex(list, 0, false);
    state.syncDots();
    startCarouselAuto(list, state, dotsWrap);
  }

  function fillCartRecommendations() {
    var wrapper = document.querySelector('[data-sis-cart-recommendations]');
    var list = document.querySelector('[data-sis-cart-recommendations-list]');
    if (!wrapper || !list) return;

    var limit = parseInt(wrapper.getAttribute('data-sis-cart-recommendations-limit'), 10) || 4;
    var moneyFormat = wrapper.getAttribute('data-sis-money-format') || '{{amount}} RON';
    var excluded = (wrapper.getAttribute('data-sis-cart-recommendations-excluded-ids') || '')
      .split(',')
      .filter(Boolean);
    var seeds = (wrapper.getAttribute('data-sis-cart-recommendations-seed-ids') || '')
      .split(',')
      .filter(Boolean);

    var shown = list.children.length;
    if (shown >= limit || seeds.length === 0) {
      wrapper.hidden = shown === 0;
      initRecommendationsCarousel();
      return;
    }

    var shownIds = excluded.slice();
    Array.prototype.forEach.call(list.children, function (child) {
      var id = child.getAttribute('data-sis-cart-rec-id');
      if (id) shownIds.push(id);
    });

    var seedIndex = 0;

    function tryNextSeed() {
      if (shown >= limit || seedIndex >= seeds.length) {
        wrapper.hidden = list.children.length === 0;
        initRecommendationsCarousel();
        return;
      }
      var productId = seeds[seedIndex++];
      var remaining = limit - shown;

      fetch('/recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=' + (remaining + shownIds.length) + '&intent=related')
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          var products = (data && data.products) || [];
          for (var i = 0; i < products.length && shown < limit; i++) {
            var product = products[i];
            var idStr = String(product.id);
            if (shownIds.indexOf(idStr) !== -1) continue;
            shownIds.push(idStr);
            list.insertAdjacentHTML('beforeend', upsellCardMarkup(product, moneyFormat));
            shown++;
          }
          tryNextSeed();
        })
        .catch(function () {
          tryNextSeed();
        });
    }

    tryNextSeed();
  }

  var drawerToastTimer = null;
  var drawerLastFocus = null;

  function getCartDrawer() {
    return document.querySelector('[data-sis-cart-drawer]');
  }

  function isCartDrawerOpen(drawer) {
    return !!(drawer && drawer.classList.contains('is-open'));
  }

  function openCartDrawer(opts) {
    opts = opts || {};
    var drawer = getCartDrawer();
    if (!drawer) return;

    var alreadyOpen = drawer.classList.contains('is-open');
    if (!alreadyOpen && document.activeElement) {
      drawerLastFocus = document.activeElement;
    }

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sis-cart-drawer-open');

    var closeBtn = drawer.querySelector('.sis-cart-drawer__close');
    if (opts.moveFocus !== false && closeBtn) closeBtn.focus();

    var toast = drawer.querySelector('[data-sis-cart-drawer-toast]');
    window.clearTimeout(drawerToastTimer);
    if (toast) {
      if (opts.showToast) {
        toast.hidden = false;
        drawerToastTimer = window.setTimeout(function () {
          toast.hidden = true;
        }, 4000);
      } else {
        toast.hidden = true;
      }
    }
  }

  function closeCartDrawer() {
    var drawer = getCartDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sis-cart-drawer-open');
    window.clearTimeout(drawerToastTimer);
    var toast = drawer.querySelector('[data-sis-cart-drawer-toast]');
    if (toast) toast.hidden = true;
    if (drawerLastFocus && typeof drawerLastFocus.focus === 'function') {
      drawerLastFocus.focus();
    }
    drawerLastFocus = null;
  }

  window.SisCart = {
    addItem: addItem,
    trackingProperties: trackingProperties,
    updateItem: changeItem,
    removeItem: removeItem,
    clearCart: clearCart,
    refreshSections: refreshSections,
    fillCartRecommendations: fillCartRecommendations,
    initRecommendationsCarousel: initRecommendationsCarousel,
    destroyRecommendationsCarousel: destroyRecommendationsCarousel,
    formatMoney: formatMoney,
    openDrawer: openCartDrawer,
    closeDrawer: closeCartDrawer
  };

  document.addEventListener('DOMContentLoaded', function () {
    fillCartRecommendations();

    document.body.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-sis-quick-add]');
      if (!btn) return;
      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      var originalHtml = btn.innerHTML;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');

      addItem(variantId, 1, { properties: trackingProperties(btn) })
        .then(function () {
          btn.textContent = '✓';
          window.setTimeout(function () {
            if (!btn.isConnected) return;
            btn.innerHTML = originalHtml;
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
          }, 1500);
        })
        .catch(function () {
          btn.innerHTML = originalHtml;
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          alert('Nu am putut adăuga produsul în coș. Încearcă din nou.');
        });
    });

    // --- Cart-page quantity steppers, remove, and empty-cart (AJAX) ---
    document.body.addEventListener('click', function (e) {
      var stepBtn = e.target.closest('[data-sis-qty-increase], [data-sis-qty-decrease]');
      if (stepBtn) {
        var stepper = stepBtn.closest('[data-sis-cart-key]');
        var input = stepper && stepper.querySelector('[data-sis-qty-input]');
        if (!stepper || !input) return;
        var delta = stepBtn.hasAttribute('data-sis-qty-increase') ? 1 : -1;
        var value = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
        input.value = value;
        var root = stepper.closest('[data-sis-section-id]');
        setPending(root, true);
        changeItem(stepper.getAttribute('data-sis-cart-key'), value).catch(function () {
          setPending(root, false);
          alert('Nu am putut actualiza cantitatea. Încearcă din nou.');
        });
        return;
      }

      var removeBtn = e.target.closest('[data-sis-cart-remove]');
      if (removeBtn) {
        var itemRow = removeBtn.closest('[data-sis-cart-key]');
        if (!itemRow) return;
        var removeRoot = itemRow.closest('[data-sis-section-id]');
        setPending(removeRoot, true);
        removeItem(itemRow.getAttribute('data-sis-cart-key')).catch(function () {
          setPending(removeRoot, false);
          alert('Nu am putut elimina produsul. Încearcă din nou.');
        });
        return;
      }

      var clearBtn = e.target.closest('[data-sis-cart-clear]');
      if (clearBtn) {
        var clearRoot = clearBtn.closest('[data-sis-section-id]');
        setPending(clearRoot, true);
        clearCart().catch(function () {
          setPending(clearRoot, false);
          alert('Nu am putut goli coșul. Încearcă din nou.');
        });
        return;
      }

      var discountBtn = e.target.closest('[data-sis-discount-apply]');
      if (discountBtn) {
        applyDiscountFromForm(discountBtn.closest('[data-sis-discount-form]'));
      }
    });

    document.body.addEventListener('change', function (e) {
      var input = e.target.closest('[data-sis-qty-input]');
      if (!input) return;
      var stepper = input.closest('[data-sis-cart-key]');
      if (!stepper) return;
      var value = Math.max(0, parseInt(input.value, 10) || 0);
      input.value = value;
      var root = stepper.closest('[data-sis-section-id]');
      setPending(root, true);
      changeItem(stepper.getAttribute('data-sis-cart-key'), value).catch(function () {
        setPending(root, false);
        alert('Nu am putut actualiza cantitatea. Încearcă din nou.');
      });
    });

    document.body.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var input = e.target.closest('[data-sis-discount-input]');
      if (!input) return;
      e.preventDefault();
      applyDiscountFromForm(input.closest('[data-sis-discount-form]'));
    });
  });

  function applyDiscountFromForm(form) {
    if (!form) return;
    var input = form.querySelector('[data-sis-discount-input]');
    var code = input && input.value.trim();
    if (!code) return;
    window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(window.location.pathname);
  }

  document.addEventListener(
    'click',
    function (e) {
      var cartIcon = e.target.closest('.sf-cart-icon');
      if (!cartIcon || !getCartDrawer()) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      openCartDrawer();
    },
    true
  );

  document.addEventListener('click', function (e) {
    var openTrigger = e.target.closest('[data-sis-cart-drawer-open]');
    if (openTrigger) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      openCartDrawer();
      return;
    }

    if (e.target.closest('[data-sis-cart-drawer-close]')) {
      closeCartDrawer();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (!isCartDrawerOpen(getCartDrawer())) return;
    closeCartDrawer();
  });

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:select', function (event) {
      if (!event.target || !event.target.querySelector) return;
      if (event.target.querySelector('[data-sis-cart-drawer]')) {
        openCartDrawer({ moveFocus: false });
      }
    });
  }

  document.addEventListener('sis:cart:updated', function (e) {
    var cart = (e.detail && e.detail.cart) || {};
    var counts = document.querySelectorAll('[data-sis-cart-count], .sf-cart-count');
    counts.forEach(function (el) {
      el.textContent = cart.item_count;
      if (el.hasAttribute('data-sis-cart-count')) el.hidden = cart.item_count === 0;
    });

    var addedItem = e.detail && e.detail.addedItem;
    var wasOpen = isCartDrawerOpen(getCartDrawer());

    refreshSections().then(function () {
      if (addedItem && !(e.detail && e.detail.skipDrawer)) {
        openCartDrawer({ showToast: true });
        return;
      }
      if (wasOpen) openCartDrawer({ moveFocus: false });
    });
  });

  /*
    Theme pages still call MinimogTheme.Cart.openCartDrawer and
    Shopify.onItemAdded / onCartUpdate. When the SIS drawer is on the page,
    those paths refresh and open it instead. The theme drawer markup is left
    in place. On the cart template the theme already skips its drawer.
  */
  function bridgeThemeCart() {
    if (!getCartDrawer() || !window.Shopify || !window.MinimogTheme || !window.MinimogTheme.Cart) return false;
    var Cart = window.MinimogTheme.Cart;
    if (Cart.__sisPatched) return true;
    if (typeof Cart.openCartDrawer !== 'function') return false;
    if (typeof Shopify.onItemAdded !== 'function' || typeof Shopify.onCartUpdate !== 'function') return false;

    var originalOpen = Cart.openCartDrawer;
    Cart.openCartDrawer = function (e) {
      if (Cart.isCartPage || !getCartDrawer()) return originalOpen.call(Cart, e);
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      refreshSections().then(function () {
        openCartDrawer();
      });
    };

    var originalAdded = Shopify.onItemAdded;
    Shopify.onItemAdded = function (line_item, open_drawer) {
      if (!getCartDrawer() || Cart.isCartPage) return originalAdded.apply(Shopify, arguments);
      if (typeof open_drawer === 'undefined') open_drawer = true;
      var result = originalAdded.call(Shopify, line_item, false);
      var finish = function () {
        if (!open_drawer) return;
        refreshSections().then(function () {
          openCartDrawer({ showToast: true });
        });
      };
      if (result && typeof result.then === 'function') return result.then(finish, finish);
      finish();
      return result;
    };

    var originalUpdate = Shopify.onCartUpdate;
    Shopify.onCartUpdate = function (cart, open_drawer) {
      if (!getCartDrawer() || Cart.isCartPage) return originalUpdate.apply(Shopify, arguments);
      if (typeof open_drawer === 'undefined') open_drawer = true;
      var result = originalUpdate.call(Shopify, cart, false);
      var finish = function () {
        if (!open_drawer) return;
        refreshSections().then(function () {
          openCartDrawer();
        });
      };
      if (result && typeof result.then === 'function') return result.then(finish, finish);
      finish();
      return result;
    };

    Cart.__sisPatched = true;
    return true;
  }

  if (!bridgeThemeCart()) {
    var bridgeTries = 0;
    var bridgeTimer = window.setInterval(function () {
      bridgeTries += 1;
      if (bridgeThemeCart() || bridgeTries > 40) window.clearInterval(bridgeTimer);
    }, 50);
  }
})();
